import React, { Suspense, lazy, useEffect } from 'react';
import { Route, useLocation, useHistory, Redirect } from 'react-router-dom';
import {
  checkIsModal,
  ROUTES,
  PRIVATE_ROUTES,
  usePreviousNormalLocation,
} from '~/Routes';
import PrivacyDialog from './PrivacyDialog';
import Header from '../components/Header';
import Footer from './Footer';
import { toast } from 'react-toastify';
import { useStoreState, useStoreActions } from '~/store';
import { useIntl } from 'react-intl';
import ScrollTopButton from 'Components/ScrollTopButton';
import { useNotifications } from '~/notifications';
import TopNav from 'Components/TopNav';
import { RoutingTable, matchUrl } from '~/router';
import { Loading } from 'Components/nui';
import Banner from './Banner';
import { useLazyRoutes } from '~/router/useLazyRoutes';
import Home from './Home';
import {
  extractSkinFromPath,
  normalizeSkin,
  getModalBackgroundLocation,
} from '~/router/solutionUrl';

// Lazy so their large page styles ship in the deferred app.scss bundle (imported by
// these chunks) rather than the render-blocking critical stylesheet.
const Market = lazy(() => import('./Market'));
const Tenders = lazy(() => import('./Tenders'));
const Modals = lazy(() => import('./Modals'));

const Body = ({ home }: { home: boolean }) => {
  const lazyRoutes = useLazyRoutes();
  const solution = useStoreState((state) => state.auth.solution);
  const user = useStoreState((state) => state.auth.user);
  const solutionSkin = normalizeSkin(solution?.skin);
  const { formatMessage } = useIntl();

  const setParticipantToast = useStoreActions(
    (state) => state.auth.setParticipantToast
  );

  const location = useLocation();
  const history = useHistory();

  const prevLocation =
    usePreviousNormalLocation(location) ||
    getModalBackgroundLocation(location.pathname);

  const isModal = checkIsModal(location.pathname);

  useEffect(() => {
    if ((solution?.new_participants ?? 0) > 0) {
      const toastId = toast(
        formatMessage(
          {
            id: 'new-participants-toast',
            defaultMessage:
              'There {count, plural, one {is # new participant} other {are # new participants}} on the platform. Click here to review them.',
          },
          {
            count: solution.new_participants,
          }
        ),
        {
          autoClose: false,
          onClick: () => {
            history.push('/participants?dismissed=0');
          },
        }
      );
      setParticipantToast(toastId);
    }
  }, [solution]);

  return (
    <>
      <section id="content" className={home ? '' : 'main-content'} role="main">
        <main
          style={{
            '--optional-text': `"${formatMessage({
              id: 'form-optional',
              defaultMessage: 'Optional',
            })}"`,
          }}
        >
          <RoutingTable
            location={isModal ? prevLocation : location}
            lazyRoutes={lazyRoutes}
          >
            {/* Legacy /home route - redirect to landing (or solution home if no access) */}
            <Route path="/home" exact>
              {user?.landingpage_access === false ? (
                <Redirect to={solutionSkin ? `/${solutionSkin}` : '/'} />
              ) : (
                <Redirect to="/" />
              )}
            </Route>
            {/* Landing page (/) - shows Home component */}
            <Route path={PRIVATE_ROUTES.landing} exact>
              {user?.landingpage_access === false && solutionSkin ? (
                <Redirect to={`/${solutionSkin}`} />
              ) : (
                <Home />
              )}
            </Route>
            {/* Solution home (/{skin}/) or marketplace */}
            <Route
              path={[PRIVATE_ROUTES.solutionHome, ...ROUTES.marketplace].flat()}
              exact
            >
              <Suspense fallback={<Loading />}>
                <Market />
              </Suspense>
            </Route>
            <Route path={matchUrl('tenders')}>
              <Suspense fallback={<Loading />}>
                <Tenders />
              </Suspense>
            </Route>
          </RoutingTable>
        </main>
      </section>
      <Suspense fallback={null}>
        <Modals />
      </Suspense>
      <ScrollTopButton />
    </>
  );
};

export default function PageWrapper() {
  const solution = useStoreState((state) => state.auth.solution);
  const loading = useStoreState((state) => state.auth.loading);
  const privacyCheckReq = useStoreState((state) => state.auth.privacyCheckReq);
  const location = useLocation();
  const prevLocation = usePreviousNormalLocation(location);

  useNotifications();

  if (!solution) return null;

  // Blank content area while switching solutions (keep topbar visible)
  if (loading) return <TopNav />;

  // "Home" = the unskinned landing route ('/' only). Bare '/{skin}' is a
  // solution home and must render the regular header/banner/footer so users
  // see the solution nav and branding.
  const isModal = checkIsModal(location.pathname);
  const effectivePathname = isModal
    ? (prevLocation?.pathname ??
      getModalBackgroundLocation(location.pathname).pathname)
    : location.pathname;
  const { skin, path } = extractSkinFromPath(effectivePathname || '/');
  const home = !skin && path === '/';

  return (
    <>
      <TopNav />
      <Header home={home} />
      {!home && <Banner />}
      {privacyCheckReq ? <PrivacyDialog /> : <Body home={home} />}
      <Footer home={home} />
    </>
  );
}
